import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useDisclosure } from '@chakra-ui/hooks';
import { toast } from 'react-toastify';

import InvoicesTable from './components/invoicesTable';
import {
    getPaginatedUnpaidInvoices,
    getUnpaidInvoicesData,
    sendInvoiceEmail,
} from 'interfaces/invoice';
import Pagination from 'components/pagination/Pagination';
import UnpaidEmailModal from './components/UnpaidEmailModal';
import PieChartCard from './components/PieChartCard';
import GoBack from 'components/actions/GoBack';
import MiniStatistics from 'components/card/MiniStatistics';
import { MdCalendarMonth, MdEuro, MdPeople } from 'react-icons/md';
import { TbFileInvoice } from 'react-icons/tb';

const TableSkeleton = () => (
    <div className="mt-1 w-full animate-pulse rounded-[20px] border bg-white p-6 shadow-3xl shadow-shadow-500 dark:bg-navy-800">
        <div className="mb-5 flex items-center justify-between">
            <div className="h-6 w-48 rounded-lg bg-gray-100 dark:bg-navy-700" />
            <div className="flex gap-2">
                <div className="h-8 w-32 rounded-xl bg-gray-100 dark:bg-navy-700" />
                <div className="h-8 w-32 rounded-xl bg-gray-100 dark:bg-navy-700" />
                <div className="h-8 w-40 rounded-xl bg-gray-100 dark:bg-navy-700" />
            </div>
        </div>
        <div className="mb-3 border-b border-gray-100 pb-3 dark:border-white/10">
            <div className="flex gap-8">
                {['N° Facture', 'Client', 'Reste à payer', 'Retard'].map((col) => (
                    <div key={col} className="h-3 w-20 rounded bg-gray-100 dark:bg-navy-700" />
                ))}
            </div>
        </div>
        {[...Array(5)].map((_, i) => (
            <div
                key={i}
                className="flex items-center gap-8 border-l-4 border-gray-200 py-3 pl-2"
                style={{ opacity: 1 - i * 0.12 }}
            >
                <div className="flex flex-col gap-1">
                    <div className="h-2.5 w-12 rounded bg-gray-100 dark:bg-navy-700" />
                    <div className="h-3.5 w-20 rounded bg-gray-100 dark:bg-navy-700" />
                </div>
                <div className="h-3.5 w-32 rounded bg-gray-100 dark:bg-navy-700" />
                <div className="h-3.5 w-20 rounded bg-gray-100 dark:bg-navy-700" />
                <div className="h-6 w-24 rounded-full bg-gray-100 dark:bg-navy-700" />
            </div>
        ))}
    </div>
);

const UnpaidManager = () => {
    const nav = useNavigate();
    const params = useParams();
    const [invoices, setInvoices] = useState(null);
    const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(5);
    const [invoicesMeta, setInvoicesMeta] = useState(null);
    const [customersDebts, setCustomersDebts] = useState(null);
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [search, setSearch] = useState('');
    const [paymentMethod, setPaymentMethod] = useState('');

    const {
        isOpen: isEmailSenderModalOpen,
        onOpen: onEmailSenderModalOpen,
        onClose: onEmailSenderModalClose,
    } = useDisclosure();

    const onEmailModalOpen = (invoiceId: string) => {
        setSelectedInvoiceId(invoiceId);
        onEmailSenderModalOpen();
    };

    const fetchInvoices = async (
        page: number,
        limit: number,
        from?: string,
        to?: string,
        searchVal?: string,
        paymentMethodVal?: string,
    ) => {
        const month = Number(params.month);
        const year = Number(params.year);
        const res = await getPaginatedUnpaidInvoices(
            limit,
            page,
            month,
            year,
            from ?? dateFrom,
            to ?? dateTo,
            searchVal ?? search,
            paymentMethodVal ?? paymentMethod,
        );
        if (res && res.data) {
            setInvoices(res.data.data);
            setInvoicesMeta(res.data.meta);
            setPage(page);
            setLimit(limit);
        }
    };

    // Updates a single invoice in place (no refetch/resort) so the table
    // doesn't jump around right after the action that triggered the change.
    const onInvoiceUpdated = (updatedInvoice: any) => {
        const stillUnpaid = ['generated', 'sent'].includes(updatedInvoice.status);
        setInvoices((prev: any) => {
            if (!prev) return prev;
            if (!stillUnpaid) return prev.filter((inv: any) => inv._id !== updatedInvoice._id);
            // Only merge in the fields the payment/transaction endpoints actually
            // return — they don't populate `customer`, so a full spread would
            // clobber it with a raw id and break the customer name display.
            return prev.map((inv: any) =>
                inv._id === updatedInvoice._id
                    ? {
                          ...inv,
                          status: updatedInvoice.status,
                          amountPaid: updatedInvoice.amountPaid,
                          leftToPay: updatedInvoice.leftToPay,
                          transactions: updatedInvoice.transactions,
                          activeDueDate: updatedInvoice.activeDueDate,
                          activeAmountDue: updatedInvoice.activeAmountDue,
                      }
                    : inv,
            );
        });
        if (!stillUnpaid) {
            setInvoicesMeta((meta: any) =>
                meta ? { ...meta, total: Math.max(0, meta.total - 1) } : meta,
            );
        }
        initCustomerDebts();
    };

    const initCustomerDebts = async (
        from?: string,
        to?: string,
        searchVal?: string,
        paymentMethodVal?: string,
    ) => {
        const month = Number(params.month);
        const year = Number(params.year);
        const res = await getUnpaidInvoicesData(
            month,
            year,
            from ?? dateFrom,
            to ?? dateTo,
            searchVal ?? search,
            paymentMethodVal ?? paymentMethod,
        );
        if (res && res.data) {
            setCustomersDebts(res.data);
        }
    };

    const onDateChange = (from: string, to: string) => {
        setDateFrom(from);
        setDateTo(to);
        fetchInvoices(1, limit, from, to);
        initCustomerDebts(from, to, search);
    };

    const onSearchChange = (newSearch: string) => {
        setSearch(newSearch);
        fetchInvoices(1, limit, dateFrom, dateTo, newSearch);
        initCustomerDebts(dateFrom, dateTo, newSearch);
    };

    const onPaymentMethodChange = (newPaymentMethod: string) => {
        setPaymentMethod(newPaymentMethod);
        fetchInvoices(1, limit, dateFrom, dateTo, search, newPaymentMethod);
        initCustomerDebts(dateFrom, dateTo, search, newPaymentMethod);
    };

    const sendEmail = async (name: string, content: string, templateName?: string) => {
        onEmailSenderModalClose();
        const result = await sendInvoiceEmail(selectedInvoiceId, name, content, templateName);
        // Sending an email doesn't change leftToPay/order, so we don't
        // refetch/resort the table for it.
        if (result?.remainingCount > 0) {
            toast.info(
                `${result.sentCount} email(s) envoyé(s), ${result.remainingCount} facture(s) restante(s) — cliquez à nouveau sur "Envoyer à tous" pour continuer.`,
            );
        }
    };

    const onFilter = async (newSearch: string, newStatus: string) => {
        if (newStatus) fetchInvoices(1, limit);
    };

    useEffect(() => {
        if (localStorage.getItem('token') === null) nav('/login');
        fetchInvoices(page, limit);
        initCustomerDebts();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const periodLabel =
        params.month === 'all' ? `${params.year}` : `${params.month}/${params.year}`;

    return (
        <div className="mt-5 md:mt-0">
            <GoBack />
            {/* KPI Cards */}
            <div className="mt-1 grid h-full w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                <MiniStatistics
                    name="Période"
                    value={periodLabel}
                    icon={<MdCalendarMonth color="#0260CB" />}
                    iconBg="bg-lightPrimary"
                />
                <MiniStatistics
                    name="Impayés sur la période"
                    value={
                        customersDebts?.totalLeftToPay != null
                            ? `${customersDebts.totalLeftToPay} €`
                            : undefined
                    }
                    icon={<MdEuro color="#0260CB" />}
                    iconBg="bg-lightPrimary"
                />
                <MiniStatistics
                    name="Factures impayées"
                    value={customersDebts?.invoicesNbr}
                    icon={<TbFileInvoice color="#0260CB" />}
                    iconBg="bg-lightPrimary"
                />
                <MiniStatistics
                    name="Clients concernés"
                    value={
                        customersDebts?.customers
                            ? String(Object.keys(customersDebts.customers).length)
                            : ''
                    }
                    icon={<MdPeople color="#0260CB" />}
                    iconBg="bg-lightPrimary"
                />
            </div>

            {/* Main content */}
            <div className="mt-4 grid h-full w-full grid-cols-5 gap-5">
                <UnpaidEmailModal
                    isOpen={isEmailSenderModalOpen}
                    onClose={onEmailSenderModalClose}
                    sendEmail={(n: string, object: string, templateName?: string) =>
                        sendEmail(n, object, templateName)
                    }
                    selectedInvoiceId={selectedInvoiceId}
                />
                <div className="col-span-5 h-full w-full md:col-span-3">
                    {invoices ? (
                        <InvoicesTable
                            tableData={invoices}
                            onOpen={() => {}}
                            onEmailSenderModalOpen={onEmailModalOpen}
                            invoicesMeta={invoicesMeta}
                            onFilter={(s: string, status: string) => onFilter(s, status)}
                            onInvoiceUpdated={onInvoiceUpdated}
                            dateFrom={dateFrom}
                            dateTo={dateTo}
                            onDateChange={onDateChange}
                            onSearchChange={onSearchChange}
                            paymentMethod={paymentMethod}
                            onPaymentMethodChange={onPaymentMethodChange}
                        />
                    ) : (
                        <TableSkeleton />
                    )}
                    <Pagination
                        extra="mt-3 col-span-12"
                        text={false}
                        type="factures"
                        maxItems={invoicesMeta ? invoicesMeta.total : 12}
                        itemsPerPage={[5, 10, 15, 20]}
                        page={page}
                        onChange={(p: number, l: number) => fetchInvoices(p, l)}
                    />
                </div>
                <div className="col-span-5 h-full w-full md:col-span-2">
                    <PieChartCard data={customersDebts?.customers} />
                </div>
            </div>
        </div>
    );
};

export default UnpaidManager;
